import { api } from '../api';

import type { PaginatedRequestParams, PaginatedResponse } from '@/types/api';
import type { Company } from '@/types/directory/Company';
import type { User, UserPermissions } from '@/types/directory/User';

const COMPANY_USERS_MAX_PAGE_SIZE = 50;

export const fetchAllPages = async ({
    url,
    params,
    signal,
}: {
    url: string;
    params?: Record<string, unknown>;
    signal?: AbortSignal;
}): Promise<User[]> => {
    let pageNumber = 1;
    let hasNextPage = true;
    const users: User[] = [];

    while (hasNextPage) {
        const response = await api.get(url, {
            params: { ...params, pageNumber, pageSize: COMPANY_USERS_MAX_PAGE_SIZE },
            signal,
        });

        const paginatedResponse: PaginatedResponse<User> = response.data;
        users.push(...paginatedResponse.items);

        hasNextPage = paginatedResponse.hasNextPage;
        pageNumber += 1;
    }

    return users;
};

export default {
    async getCompanyInformation(): Promise<Company> {
        const response = await api.get('/directory/company/me');

        return response.data;
    },

    async getCompanyEmployees(params?: PaginatedRequestParams): Promise<PaginatedResponse<User>> {
        const response = await api.get('/directory/user/me/employee', { params });

        return response.data;
    },

    async getUsersByIds(userIds: string[]): Promise<User[]> {
        const response = await api.post('/directory/user/byids', { userIds });

        return response.data;
    },

    async getUserById(userId: string): Promise<User> {
        const response = await api.get(`/directory/user/${userId}`);

        return response.data;
    },

    async getCompanyUsers(params?: PaginatedRequestParams): Promise<PaginatedResponse<User>> {
        const response = await api.get(`/directory/user`, { params });

        return response.data;
    },

    async getAllCompanyUsers({
        isActiveOnly,
        includeInvited,
        signal,
    }: {
        isActiveOnly?: boolean;
        includeInvited?: boolean;
        signal?: AbortSignal;
    } = {}): Promise<User[]> {
        const params: Record<string, unknown> = {};
        if (isActiveOnly !== undefined) params.isActiveOnly = isActiveOnly;
        if (includeInvited !== undefined) params.includeInvited = includeInvited;
        return fetchAllPages({ url: '/directory/user', params, signal });
    },

    async getAllManagerEmployees({
        signal,
    }: {
        signal?: AbortSignal;
    } = {}): Promise<User[]> {
        return fetchAllPages({
            url: '/directory/user/me/employee',
            params: { isActiveOnly: true, includeInvited: true } as Record<string, unknown>,
            signal,
        });
    },

    /**
     * Fetches all employees in the current user's downward hierarchy (direct + indirect reports).
     * Uses a client-side recursive walk to include all levels of the hierarchy.
     * @param options.includeSelf - If true, includes the current user in the result
     * @param options.signal - AbortSignal for cancellation
     */
    async getAllHierarchyEmployees({
        includeSelf = false,
        signal,
    }: {
        includeSelf?: boolean;
        signal?: AbortSignal;
    } = {}): Promise<User[]> {
        // Fetch all company employees to build the hierarchy
        const allEmployees = await this.getAllCompanyUsers({
            isActiveOnly: true,
            includeInvited: true,
            signal,
        });

        // Get the current user's ID to start the hierarchy traversal
        const currentUser = await this.getUserById('me');

        // Recursively collect all direct and indirect reports (DOWNWARD only)
        const hierarchyEmployeeIds = new Set<string>();

        const collectSubEmployees = (parentId: string) => {
            // Find employees whose manager is the parentId (these are direct reports of parentId)
            const directReports = allEmployees.filter((emp) => emp.managerId === parentId);
            directReports.forEach((emp) => {
                if (!hierarchyEmployeeIds.has(emp.id)) {
                    hierarchyEmployeeIds.add(emp.id);
                    // Recursively find reports of this employee
                    collectSubEmployees(emp.id);
                }
            });
        };

        // Start from current user - find all employees who report to current user (and their reports)
        collectSubEmployees(currentUser.id);

        // Filter employees to only include those in the hierarchy
        const result = allEmployees.filter((user) => hierarchyEmployeeIds.has(user.id));

        // Optionally include self
        if (includeSelf) {
            const selfInResult = result.find((u) => u.id === currentUser.id);
            if (!selfInResult) {
                result.unshift(currentUser);
            }
        }

        return result;
    },

    async getUserPermissions(): Promise<UserPermissions> {
        const response = await api.get(`/directory/user/me/permission`);

        return response.data;
    },
};
